import { downloadAuthorizedFile, request } from './core.api';
import type { BillingPlanDto } from './billing.api';

export interface AdminUserDto {
  id: string;
  externalId: string;
  email: string;
  effectiveDisplayName: string;
  createdAt: string;
  lastLoggedIn: string | null;
  isBlocked: boolean;
  newSubscriptionBlocked?: boolean;
  onboardingCompleted: boolean;
  isBusinessAccount: boolean;
  provider: string;
  role: number | string;
  subscriptionPlanKey?: string | null;
  subscriptionInterval?: string | null;
  subscriptionStatus?: string | null;
  subscriptionStartedAtUtc?: string | null;
  subscriptionCurrentPeriodEndUtc?: string | null;
  paymentProviderKey?: string | null;
  paymentProviderCustomerId?: string | null;
  hasManageableSubscription?: boolean;
}

export interface SetAdminUserSubscriptionRequest {
  planKey: string;
  billingInterval: 'monthly' | 'yearly';
  startsAtUtc: string;
  endsAtUtc: string;
}

export interface SetAdminUserBlockedRequest {
  isBlocked: boolean;
}

export interface SetNewUserAccessRequest {
  isEnabled: boolean;
}

export interface SetAdvertisingEnabledRequest {
  isEnabled: boolean;
}

export interface SetAllowedCountryCodesRequest {
  countryCodes: string[];
}

export interface SetExternalLoginAccessRequest {
  isEnabled: boolean;
}

export interface SetNewSubscriptionAccessRequest {
  isEnabled: boolean;
}

export interface ResetPlatformRequest {
  confirmation: string;
}

export interface ForceCancelPaidSubscriptionsRequest {
  userIds?: string[];
  applyToAllPaidUsers: boolean;
  confirmation?: string;
}

export interface ForceCancelPaidSubscriptionsResultDto {
  processedCount: number;
  skippedCount: number;
}

export interface AdminUsersQueryParams {
  name?: string;
  email?: string;
  id?: string;
  business?: boolean;
  onboarding?: boolean;
  page?: number;
  pageSize?: number;
}

export interface AdminUsersPageDto {
  items: AdminUserDto[];
  allowedCountryCodes?: string[];
  advertisingEnabled?: boolean;
  googleLoginEnabled?: boolean;
  microsoftLoginEnabled?: boolean;
  newUserAccessEnabled: boolean;
  newSubscriptionAccessEnabled?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApplicationErrorDto {
  id: string;
  occurredAtUtc: string;
  functionName: string;
  path: string | null;
  method: string | null;
  statusCode: number;
  exceptionType: string;
  message: string;
  stackTrace: string | null;
  userId: string | null;
  invocationId: string | null;
  metadataJson: string | null;
}

export interface ApplicationErrorsQueryParams {
  functionName?: string;
  path?: string;
  method?: string;
  userId?: string;
  search?: string;
  statusCode?: number;
  occurredFrom?: string;
  occurredTo?: string;
  page?: number;
  pageSize?: number;
}

export interface ApplicationErrorsPageDto {
  items: ApplicationErrorDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminRevenueYearDto {
  year: number;
  revenueInCents: number;
  invoiceCount: number;
  creditNoteCount: number;
}

export interface AdminRevenueOverviewDto {
  currency: string;
  totalRevenueInCents: number;
  items: AdminRevenueYearDto[];
}

export interface AdminBillingDocumentDto {
  id: string;
  userId?: string | null;
  firstName: string;
  lastName: string;
  number: string;
  status: string;
  totalInCents: number;
  amountPaidInCents: number;
  currency: string;
  paymentProviderKey: string;
  createdAtUtc: string;
  paidAtUtc?: string | null;
  canDownload: boolean;
}

export interface AdminBillingDocumentsPageDto {
  items: AdminBillingDocumentDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminBillingDocumentsQueryParams {
  page?: number;
  pageSize?: number;
}

export interface UpdateBillingPlanConfigurationRequest {
  monthlyPriceInCents: number;
  yearlyPriceInCents: number;
  propertyLimit: number;
  storageLimitBytes: number;
}

export const adminApi = {
  getUsers: (params: AdminUsersQueryParams = {}) => {
    const query = new URLSearchParams();

    if (params.name) query.set('name', params.name);
    if (params.email) query.set('email', params.email);
    if (params.id) query.set('id', params.id);
    if (params.business !== undefined) query.set('business', String(params.business));
    if (params.onboarding !== undefined) query.set('onboarding', String(params.onboarding));
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<AdminUsersPageDto>(`/platform/users${suffix}`);
  },
  getApplicationErrors: (params: ApplicationErrorsQueryParams = {}) => {
    const query = new URLSearchParams();

    if (params.functionName) query.set('functionName', params.functionName);
    if (params.path) query.set('path', params.path);
    if (params.method) query.set('method', params.method);
    if (params.userId) query.set('userId', params.userId);
    if (params.search) query.set('search', params.search);
    if (params.statusCode !== undefined) query.set('statusCode', String(params.statusCode));
    if (params.occurredFrom) query.set('occurredFrom', params.occurredFrom);
    if (params.occurredTo) query.set('occurredTo', params.occurredTo);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<ApplicationErrorsPageDto>(`/platform/errors${suffix}`);
  },
  deleteApplicationError: (id: string) => request<void>(`/platform/errors/${id}`, {
    method: 'DELETE',
  }),
  deleteUser: (userId: string) => request<void>(`/platform/users/${userId}`, {
    method: 'DELETE',
  }),
  resetPlatform: (body: ResetPlatformRequest) => request<void>('/platform/reset', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setUserBlocked: (userId: string, body: SetAdminUserBlockedRequest) => request<void>(`/platform/users/${userId}/blocked`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setAdvertising: (body: SetAdvertisingEnabledRequest) => request<void>('/platform/advertising', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setAllowedCountries: (body: SetAllowedCountryCodesRequest) => request<void>('/platform/allowed-countries', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setGoogleLoginAccess: (body: SetExternalLoginAccessRequest) => request<void>('/platform/login/google', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setMicrosoftLoginAccess: (body: SetExternalLoginAccessRequest) => request<void>('/platform/login/microsoft', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setNewUserAccess: (body: SetNewUserAccessRequest) => request<void>('/platform/new-user-access', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  setNewSubscriptionAccess: (body: SetNewSubscriptionAccessRequest) => request<void>('/platform/new-subscription-access', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  getRevenueOverview: () => request<AdminRevenueOverviewDto>('/platform/revenue'),
  getBillingPlans: () => request<BillingPlanDto[]>('/platform/billing-plans'),
  updateBillingPlan: (planKey: string, body: UpdateBillingPlanConfigurationRequest) => request<BillingPlanDto>(`/platform/billing-plans/${encodeURIComponent(planKey)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }),
  getInvoices: (params: AdminBillingDocumentsQueryParams = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<AdminBillingDocumentsPageDto>(`/platform/invoices${suffix}`);
  },
  getCreditNotes: (params: AdminBillingDocumentsQueryParams = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return request<AdminBillingDocumentsPageDto>(`/platform/credit-notes${suffix}`);
  },
  downloadBillingDocument: (documentId: string, fileName?: string) =>
    downloadAuthorizedFile(`/platform/billing-documents/${encodeURIComponent(documentId)}/download`, fileName),
  setUserSubscription: (userId: string, body: SetAdminUserSubscriptionRequest) => request<void>(`/platform/users/${userId}/subscription`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  forceCancelPaidSubscriptions: (body: ForceCancelPaidSubscriptionsRequest) => request<ForceCancelPaidSubscriptionsResultDto>('/platform/subscriptions/force-cancel', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
};
